import { Link } from "react-router-dom";
import {
  HeartHandshake,
  GraduationCap,
  Sprout,
  Users,
  Sun,
  Sparkles,
  ShieldCheck,
  Trophy,
  Puzzle,
  Globe,
  ArrowRight,
} from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const AREAS = [
  {
    icon: HeartHandshake,
    title: "Mental Wellness",
    text: "Promoting awareness around mental and emotional well-being through educational content, awareness activities and conversations that encourage healthier perspectives.",
  },
  {
    icon: GraduationCap,
    title: "Educational Support",
    text: "Encouraging learning by sharing educational resources and creating opportunities for knowledge and skill development.",
  },
  {
    icon: Sprout,
    title: "Personal Development",
    text: "Encouraging self-awareness, confidence, communication, positive habits and continuous personal growth.",
  },
  {
    icon: Users,
    title: "Community Initiatives",
    text: "Creating opportunities for people to connect, participate and support one another.",
  },
  {
    icon: Sun,
    title: "Positive Living",
    text: "Encouraging balanced routines, meaningful connections and positive approaches to everyday life.",
  },
];

const EDU_CARDS = [
  {
    icon: Sparkles,
    title: "Make Learning Joyful",
    text: "Encourage curiosity, creativity, exploration and active participation.",
  },
  {
    icon: ShieldCheck,
    title: "Create Supportive Environments",
    text: "Help learners feel respected, safe to ask questions and comfortable making mistakes.",
  },
  {
    icon: Trophy,
    title: "Celebrate Growth",
    text: "Recognize effort, improvement, persistence and progress — not only results.",
  },
  {
    icon: Puzzle,
    title: "Build Life Skills",
    text: "Develop communication, critical thinking, problem-solving, teamwork and adaptability alongside academic knowledge.",
  },
  {
    icon: Globe,
    title: "Connect Learning With Real Life",
    text: "Help learners understand how education connects with their communities and everyday lives.",
  },
];

export default function WhatWeDo() {
  return (
    <>
      <SEO
        title="What We Do | Nurturing Happiness"
        description="Our work focuses on mental wellness, educational support, personal development, community initiatives and positive living."
      />

      <section data-testid="wwd-header" className="paper-texture py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-nh-green">Our Work</p>
            <h1 className="mt-3 max-w-3xl font-heading text-4xl font-bold tracking-tight text-nh-ink sm:text-5xl">
              What We Do
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-nh-muted md:text-lg">
              Our work focuses on areas that contribute to individual well-being, personal
              development and stronger communities.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="wwd-areas" className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-5xl space-y-6 px-6 md:px-12">
          {AREAS.map((area, i) => (
            <Reveal key={area.title} delay={0.05}>
              <div
                data-testid={`wwd-area-${i + 1}`}
                className="group flex flex-col gap-6 rounded-3xl border border-nh-deep/5 bg-white p-8 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift sm:flex-row sm:items-start md:p-10"
              >
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-nh-mint text-nh-green transition-colors duration-300 group-hover:bg-nh-green group-hover:text-white">
                  <area.icon className="h-8 w-8" strokeWidth={1.75} />
                </div>
                <div>
                  <div className="flex items-baseline gap-3">
                    <span className="font-heading text-lg font-bold text-nh-gold">0{i + 1}</span>
                    <h2 className="font-heading text-2xl font-bold text-nh-ink">{area.title}</h2>
                  </div>
                  <p className="mt-3 max-w-2xl text-base leading-relaxed text-nh-muted">{area.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee />

      <section data-testid="wwd-education-feature" className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="overflow-hidden rounded-[2.5rem] bg-nh-deep shadow-lift">
            <div className="px-8 py-14 md:px-14 md:py-20">
              <Reveal>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-nh-yellow">
                  Education
                </p>
                <h2 className="mt-3 max-w-2xl font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">
                  Better Education, Happier Learners
                </h2>
                <p className="mt-5 max-w-2xl text-base leading-relaxed text-nh-cream/80 md:text-lg">
                  We believe education should go beyond examinations and academic achievement.
                  Learning becomes more meaningful when students feel supported, respected, curious
                  and motivated.
                </p>
              </Reveal>
              <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {EDU_CARDS.map((card, i) => (
                  <Reveal key={card.title} delay={i * 0.07}>
                    <div
                      data-testid={`edu-card-${i + 1}`}
                      className="h-full rounded-3xl bg-white/[0.07] p-7 ring-1 ring-white/10 transition-all duration-300 hover:-translate-y-1.5 hover:bg-white/[0.12]"
                    >
                      <card.icon className="h-7 w-7 text-nh-yellow" strokeWidth={1.75} />
                      <h3 className="mt-4 text-sm font-bold uppercase tracking-[0.12em] text-white">
                        {card.title}
                      </h3>
                      <p className="mt-3 text-sm leading-relaxed text-nh-cream/75">{card.text}</p>
                    </div>
                  </Reveal>
                ))}
                <Reveal delay={0.35}>
                  <Link
                    to="/10-tips-for-better-education"
                    data-testid="wwd-article-cta"
                    className="flex h-full min-h-[180px] flex-col justify-between rounded-3xl bg-nh-gold p-7 text-nh-ink transition-all duration-300 hover:-translate-y-1.5 hover:bg-nh-yellow"
                  >
                    <p className="font-heading text-xl font-semibold leading-snug">
                      10 Tips for Better Education
                    </p>
                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold">
                      Read 10 Tips for Better Education <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="wwd-closing" className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-6 text-center md:px-12">
          <Reveal>
            <h2 className="font-heading text-3xl font-bold tracking-tight text-nh-ink sm:text-4xl">
              Small Steps. Meaningful Change.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-nh-muted md:text-lg">
              Positive change does not always begin with something big. It can begin with learning
              something new, taking care of yourself, helping someone around you or simply creating
              a supportive environment.
            </p>
            <Link
              to="/contact"
              data-testid="wwd-get-in-touch-btn"
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-nh-green px-9 py-3.5 text-[15px] font-semibold text-white shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:bg-nh-deep hover:shadow-lift active:scale-95"
            >
              Get in Touch <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
